// @ts-check

import { merge, isEmpty, get, set } from "lodash";

/**
 * @typedef {(path: string, sources: Object[]) => any} Resolver
 * @typedef {{path: string, resolver: Resolver}} Override
 */

/**
 * Merge normally and then apply custom overrides to target fields.
 *
 * @param {Override[]} overrides Overrides to apply after the regular merge, see {@link MergeResolvers} for some common resolvers.
 * @param {Object} defaults Defaults for the merge, and last object in resolver `sources`.
 * @param {Object[]} objects Objects to merge with the `defaults`. Order affects result (unless affected by `overrides`).
 *
 * @returns {Record<string, any>} The final merged object.
 */
const mergeWithResolvers = (overrides, defaults, ...objects) => {
  const sources = [...objects, defaults];

  // start off with a normal merge as the baseline (i.e. only undefined doesn't override)
  const merged = merge({}, defaults, ...objects);

  // iter overrides and resolve new values
  for (const { path, resolver } of overrides) {
    set(merged, path, resolver(path, sources));
  }

  return merged;
};

/**
 * @readonly @satisfies {Record<string, Resolver>}
 */
const MergeResolvers = {
  /**
   * The first non-empty value from iterating all the objects in supplied order.
   * @param {string} path
   * @param {Object[]} sources
   * @returns {any}
   */
  firstNonEmpty: (path, sources) => {
    for (const src of sources) {
      const val = get(src, path);
      if (!isEmpty(val)) {
        return val;
      }
    }
    return undefined;
  },
  /**
   * The last non-empty value from iterating all the objects in supplied order.
   * @param {string} path
   * @param {Object[]} sources
   * @returns {any}
   */
  lastNonEmpty: (path, sources) => {
    let last;
    for (const src of sources) {
      const val = get(src, path);
      if (!isEmpty(val)) {
        last = val;
      }
    }
    return last;
  },
};

export { mergeWithResolvers, MergeResolvers };
